/** @format */

import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import StepperButtons from '../../../common/custom/StepperButtons';
import { FormikProps, useFormik } from 'formik';
import { StudyDetailsDto } from '../Models';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectConstants } from '../../../common/dropdowns/Api/constantSlice';
import * as yup from 'yup';
import UniversityMultiselect from '../../../common/dropdowns/UniversityMultiSelect';
import YearOfStudy from '../../../common/dropdowns/YearOfStudt';
import TypeOfStudy from '../../../common/dropdowns/TypeOfStudy';
import { addStudyDetailsThunk, selectApplicant } from '../ApplicantSlice';
import { FeedbackDto } from '../../../common/models/requests/FeedbackDto';
import { GetMessageReponse } from '../../../common/Logic/ServerResponse';
import MessageResponse from '../../../common/feedback/messageResponse';
import SavedApplicationPopUp from './SavedApplicationPopUp';
import { DropdownDto } from '../../../common/dropdowns/Api/Models';
import {
  EDUCATION_HDI_INSTITUTION_IDS_ORDERED,
  FOURTH_YEAR_BURSARY_YEAR_OF_STUDY_ID,
  isEducationHdi,
  isPgdaHdiApplicant,
  isSingleCurrentInstitutionStudyLock,
  PGDA_YEAR_OF_STUDY_ID,
} from '../pgdaConstants';

interface Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  activeStep: number;
}

export default function StudyDetails({ setActiveStep, activeStep }: Props) {
  const [isSaveAndClose, setIsSaveAndClose] = useState<boolean>(false);
  const applicant = useAppSelector(selectApplicant);
  const selData = useAppSelector(selectConstants);
  const dispatch = useAppDispatch();

  const getEducationInstitutionId = (): number =>
    applicant.loginState?.accessToken?.application?.education?.institutionId ??
    0;

  const getEducationYearOfStudyId = (): number => {
    const raw =
      applicant.loginState?.accessToken?.application?.education?.yearOfStudyId;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const maxUniversitiesBeforePgda = (): number => {
    const user = applicant.loginState?.accessToken?.application;
    if (!user?.education) return 3;
    const education = user.education;
    const id = education.institutionId;
    if (isEducationHdi(id) || id === 21) {
      return 3;
    }
    const yearOfStudyId = education.yearOfStudyId;
    if (yearOfStudyId === 1 || yearOfStudyId === 2 || yearOfStudyId === 3) {
      return 2;
    }
    return 3;
  };

  const resolveMaxUniversities = (yearOfStudyId: number): number => {
    const eduId = getEducationInstitutionId();
    if (yearOfStudyId === PGDA_YEAR_OF_STUDY_ID) {
      if (eduId <= 0) return 1;
      return 2;
    }
    if (eduId > 0 && isSingleCurrentInstitutionStudyLock(yearOfStudyId, eduId)) {
      return 1;
    }
    return maxUniversitiesBeforePgda();
  };

  const validationSchema = yup.object({
    qualificationStartYear: yup
      .number()
      .required('Bursary application start year is required')
      .integer('Bursary application start year must be a whole number')
      .min(2000, 'Year does not look valid')
      .max(2100, 'Year does not look valid'),
    typeOfStudyId: yup
      .number()
      .required('Type of study is required')
      .min(1, 'Type of study is required'),
    institutions: yup
      .array()
      .min(1, 'Please select at least one university')
      .when(['yearOfStudyId'], ([y], schema) => {
        const year = Number(y);
        const eduId = getEducationInstitutionId();
        if (year === PGDA_YEAR_OF_STUDY_ID) {
          const eduYear = getEducationYearOfStudyId();
          if (
            isEducationHdi(eduId) &&
            (isPgdaHdiApplicant(eduId) || eduYear === 4)
          ) {
            const hdi =
              EDUCATION_HDI_INSTITUTION_IDS_ORDERED as readonly number[];
            return schema
              .max(
                2,
                'You can select at most two universities for your PGDA application.'
              )
              .min(
                1,
                'Please select at least one university (other than Zululand, Venda, or University of Limpopo).'
              )
              .test(
                'pgda-hdi-other-unis-not-trio',
                'University of Zululand, University of Venda, and University of Limpopo cannot be selected here. Choose up to two other universities.',
                (arr) => {
                  if (!Array.isArray(arr) || arr.length === 0) return false;
                  return arr.every(
                    (row: DropdownDto) =>
                      row?.id != null && !hdi.includes(row.id as number)
                  );
                }
              );
          }
          return schema
            .max(
              2,
              'For PGDA you may select your current tertiary institution and at most one additional university.'
            )
            .test(
              'pgda-non-hdi-includes-current',
              'Your current tertiary institution (from the Education step) must be included for PGDA.',
              (arr) => {
                if (!Array.isArray(arr)) return false;
                return arr.some((row: DropdownDto) => row?.id === eduId);
              }
            );
        }
        if (eduId > 0 && isSingleCurrentInstitutionStudyLock(year, eduId)) {
          return schema
            .max(
              1,
              'You may only select one university for this application.'
            )
            .test(
              'continuing-single-uni',
              'It must be the same tertiary institution you entered on the Education step.',
              (arr) => {
                if (!Array.isArray(arr) || arr.length !== 1) return false;
                const row = arr[0] as DropdownDto;
                return row?.id === eduId;
              }
            );
        }
        const m = resolveMaxUniversities(year);
        return schema.max(
          m,
          `You can only select 1 to ${m} universities`
        );
      }),
    yearOfStudyId: yup
      .number()
      .required('Year of study is required')
      .min(1, 'Year of study is required'),
    firmName: yup.string().when(['hasExistingContract', 'yearOfStudyId'], {
      is: (hasContract: boolean, yearId: number) =>
        hasContract === true && Number(yearId) === PGDA_YEAR_OF_STUDY_ID,
      then: (schema) =>
        schema.required(
          'Please enter the name of the firm where you have a training contract.'
        ),
    }),
  });
  const [feedback, setFeedback] = useState<FeedbackDto>({} as FeedbackDto);
  const [formValues, setFormValues] = useState<StudyDetailsDto>();
  const [isSaveAndCloseMessage, setIsSaveAndCloseMessage] =
    useState<string>('');
  const [isPrefilledFromPreviousYear, setIsPrefilledFromPreviousYear] =
    useState<boolean>(false);

  const initialValues = {
    qualificationStartYear: new Date().getFullYear() + 1,
    typeOfStudyId: 1,
    institutions: [],
    yearOfStudyId: 0,
    hasExistingContract: false,
    firmName: '',
  };
  const formik: FormikProps<StudyDetailsDto> = useFormik<StudyDetailsDto>({
    initialValues: formValues || initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values: StudyDetailsDto) => {
      let institutionsList = [];
      for (let i = 0; i < values.institutions.length; i++) {
        institutionsList.push(values.institutions[i].id);
      }
      values.institutions = institutionsList as any;
      dispatch(addStudyDetailsThunk(values)).then((resp: any) => {
        const payload = resp?.payload;
        const v = payload?.validation ?? payload?.Validation;
        const code = v?.code ?? v?.Code;
        const fulfilled =
          resp?.meta?.requestStatus === 'fulfilled' ||
          String(resp?.type ?? '').endsWith('/fulfilled');
        if (fulfilled && (code === 200 || code === 201)) {
          setIsPrefilledFromPreviousYear(false);
          if (isSaveAndClose) {
            setIsSaveAndCloseMessage(
              'Your application was saved successfully, you can come back later to complete your application. Press close application to log out and keep your information secure'
            );
          } else {
            setActiveStep(activeStep + 1);
          }
        }
      });
    },
  });

  React.useEffect(() => {
    if (
      applicant.applicantCreateState.status === 'failed' &&
      applicant.applicantCreateState.error
    ) {
      const feed = GetMessageReponse(
        applicant.applicantCreateState.error,
        false,
        'fulfilled'
      );
      setFeedback(feed);
      return;
    }
    if (applicant.loginState.status === 'failed') {
      const feed = GetMessageReponse(
        applicant.loginState.error,
        false,
        'fulfilled'
      );
      setFeedback(feed);
      return;
    }
  }, [applicant.applicantCreateState.status, applicant.loginState.status]);

  /** Maps education step year-of-study to the bursary "year applying for" dropdown. */
  const deriveYearOfStudyForStudyStep = (
    educationYearId: number,
    isTakingGapYear: boolean | undefined,
    educationInstitutionId: number
  ) => {
    if (isTakingGapYear === true && educationYearId === 1) return 1;
    if (educationYearId === 1) return 2;
    if (educationYearId === 2) return 3;
    if (educationYearId === 3) {
      if (isPgdaHdiApplicant(educationInstitutionId)) {
        return FOURTH_YEAR_BURSARY_YEAR_OF_STUDY_ID;
      }
      return PGDA_YEAR_OF_STUDY_ID;
    }
    if (educationYearId === 4) {
      return PGDA_YEAR_OF_STUDY_ID;
    }
    return 1;
  };

  const stripFourthYearHdiInvalidUniversities = () => {
    const eduId = getEducationInstitutionId();
    if (
      formik.values.yearOfStudyId !== PGDA_YEAR_OF_STUDY_ID ||
      eduId <= 0 ||
      !isEducationHdi(eduId) ||
      !(isPgdaHdiApplicant(eduId) || getEducationYearOfStudyId() === 4)
    ) {
      return;
    }
    const hdi = new Set<number>(
      EDUCATION_HDI_INSTITUTION_IDS_ORDERED as readonly number[]
    );
    const inst = (formik.values.institutions as DropdownDto[]) ?? [];
    const cleaned = inst.filter((i) => !hdi.has(i.id));
    if (cleaned.length !== inst.length) {
      formik.setFieldValue('institutions', cleaned);
    }
  };

  const syncPgdaNonHdiInstitution = () => {
    const eduId = getEducationInstitutionId();
    const uniList = selData.constantState?.universities;
    if (
      formik.values.yearOfStudyId !== PGDA_YEAR_OF_STUDY_ID ||
      eduId <= 0 ||
      isPgdaHdiApplicant(eduId) ||
      !uniList
    ) {
      return;
    }
    if (isEducationHdi(eduId) && getEducationYearOfStudyId() === 4) {
      return;
    }
    const uni = uniList.find((u) => u.id === eduId);
    if (!uni) return;
    const inst = (formik.values.institutions as DropdownDto[]) ?? [];
    if (!inst.some((i) => i.id === eduId)) {
      formik.setFieldValue('institutions', [
        uni,
        ...inst.filter((i) => i.id !== eduId),
      ]);
    }
  };

  const syncSecondThirdYearInstitution = () => {
    const eduId = getEducationInstitutionId();
    const uniList = selData.constantState?.universities;
    const y = formik.values.yearOfStudyId;
    if (
      eduId <= 0 ||
      !isSingleCurrentInstitutionStudyLock(y, eduId) ||
      !uniList
    ) {
      return;
    }
    const uni = uniList.find((u) => u.id === eduId);
    if (!uni) return;
    const inst = formik.values.institutions as DropdownDto[];
    if (inst.length !== 1 || inst[0]?.id !== eduId) {
      formik.setFieldValue('institutions', [uni]);
    }
  };

  useEffect(() => {
    syncPgdaNonHdiInstitution();
    syncSecondThirdYearInstitution();
    stripFourthYearHdiInvalidUniversities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.yearOfStudyId,
    applicant.loginState?.accessToken?.application?.education?.institutionId,
    applicant.loginState?.accessToken?.application?.education?.yearOfStudyId,
    selData.constantState?.universities,
  ]);

  const loadStudyDetails = () => {
    const progress = applicant.loginState.accessToken;
    if (!progress?.application) return;

    const app = progress.application;
    const qualificationStartYear = new Date().getFullYear() + 1;
    const eduInstId = app.education?.institutionId ?? 0;

    if (app.studyDetailsDto) {
      let institutionArray = [] as DropdownDto[];
      const derivedYearFromEducation = deriveYearOfStudyForStudyStep(
        app.education.yearOfStudyId ?? 0,
        app.education.isTakingGapYear === true,
        eduInstId
      );

      (app.studyDetailsDto.institutions ?? []).forEach(
        (item: DropdownDto | number) => {
          const rawId =
            typeof item === 'number' ? item : (item as DropdownDto)?.id;
          const institutionFromEducation =
            selData.constantState?.universities?.find((s) => s.id === rawId);
          if (institutionFromEducation) {
            institutionArray.push(institutionFromEducation);
          }
        }
      );

      const studyRowYear = app.studyDetailsDto.yearOfStudyId;
      const yearPrefilledFromPriorYear =
        app.studyDetailsDto.isPrefilledFromPreviousYear === true;
      const yearOfStudyId = yearPrefilledFromPriorYear
        ? derivedYearFromEducation
        : studyRowYear && studyRowYear > 0
          ? studyRowYear
          : derivedYearFromEducation;

      const isPgdaExternalUniversitiesForHdi =
        yearOfStudyId === PGDA_YEAR_OF_STUDY_ID &&
        eduInstId > 0 &&
        isEducationHdi(eduInstId) &&
        (isPgdaHdiApplicant(eduInstId) || (app.education?.yearOfStudyId ?? 0) === 4);

      if (isPgdaExternalUniversitiesForHdi) {
        const hdi = new Set<number>(
          EDUCATION_HDI_INSTITUTION_IDS_ORDERED as readonly number[]
        );
        institutionArray = institutionArray.filter((i) => !hdi.has(i.id));
      }

      if (
        isSingleCurrentInstitutionStudyLock(yearOfStudyId, eduInstId) &&
        eduInstId > 0
      ) {
        const only = selData.constantState?.universities?.find(
          (s) => s.id === eduInstId
        );
        institutionArray = only ? [only] : institutionArray;
      }

      const populate = {
        institutions: institutionArray,
        qualificationStartYear,
        typeOfStudyId:
          app.studyDetailsDto.typeOfStudyId &&
          app.studyDetailsDto.typeOfStudyId > 0
            ? app.studyDetailsDto.typeOfStudyId
            : 1,
        yearOfStudyId,
        hasExistingContract: app.studyDetailsDto.hasExistingContract,
        firmName: app.studyDetailsDto.firmName,
      } as StudyDetailsDto;

      setFormValues(populate);
      setIsPrefilledFromPreviousYear(yearPrefilledFromPriorYear);
      return;
    }

    if (app.education) {
      const y = deriveYearOfStudyForStudyStep(
        app.education.yearOfStudyId ?? 0,
        app.education.isTakingGapYear === true,
        eduInstId
      );
      let institutions: DropdownDto[] = [];
      if (
        y === PGDA_YEAR_OF_STUDY_ID &&
        eduInstId > 0 &&
        isPgdaHdiApplicant(eduInstId)
      ) {
        institutions = [];
      } else if (
        y === PGDA_YEAR_OF_STUDY_ID &&
        eduInstId > 0 &&
        !isPgdaHdiApplicant(eduInstId)
      ) {
        const u = selData.constantState?.universities?.find(
          (s) => s.id === eduInstId
        );
        if (u) institutions = [u];
      } else if (
        y === PGDA_YEAR_OF_STUDY_ID &&
        eduInstId > 0 &&
        isEducationHdi(eduInstId) &&
        (app.education.yearOfStudyId ?? 0) === 4
      ) {
        institutions = [];
      } else if (
        isSingleCurrentInstitutionStudyLock(y, eduInstId) &&
        eduInstId > 0
      ) {
        const u = selData.constantState?.universities?.find(
          (s) => s.id === eduInstId
        );
        if (u) institutions = [u];
      }

      setFormValues({
        institutions,
        qualificationStartYear,
        typeOfStudyId: 1,
        yearOfStudyId: y,
        hasExistingContract: false,
        firmName: '',
      });
      setIsPrefilledFromPreviousYear(false);
    }
  };

  useEffect(() => {
    loadStudyDetails();
  }, [
    applicant.loginState.accessToken,
    selData.constantState?.universities,
  ]);

  const maxUni = resolveMaxUniversities(formik.values.yearOfStudyId);
  const eduInstForPgda = getEducationInstitutionId();
  const isPgdaNonHdiFlow =
    formik.values.yearOfStudyId === PGDA_YEAR_OF_STUDY_ID &&
    eduInstForPgda > 0 &&
    !isPgdaHdiApplicant(eduInstForPgda);
  const isPgdaExternalUniversitiesForHdi =
    formik.values.yearOfStudyId === PGDA_YEAR_OF_STUDY_ID &&
    eduInstForPgda > 0 &&
    isEducationHdi(eduInstForPgda) &&
    (isPgdaHdiApplicant(eduInstForPgda) || getEducationYearOfStudyId() === 4);
  const isSingleInstitutionLocked =
    isSingleCurrentInstitutionStudyLock(
      formik.values.yearOfStudyId,
      eduInstForPgda
    ) && eduInstForPgda > 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {isSaveAndCloseMessage && isSaveAndCloseMessage !== '' && (
          <SavedApplicationPopUp
            isSaveAndClose={isSaveAndClose}
            message={isSaveAndCloseMessage}
            status={applicant.applicantCreateState.status}
          />
        )}
        <MessageResponse feedback={feedback} />
      </Grid>
      {isPrefilledFromPreviousYear && (
        <Grid item xs={12}>
          <Alert severity='info' variant='standard'>
            We've pre-filled your study details from your previous application.
            Please review your university choices, the year you are applying
            for (for example CTA if you are now in your final undergraduate
            year), and type of study—update anything that has changed—then click
            "Save and Continue" to confirm for this year.
          </Alert>
        </Grid>
      )}
      {isPgdaNonHdiFlow && !isPgdaExternalUniversitiesForHdi && (
        <Grid item xs={12}>
          <Alert severity='info' variant='standard'>
            For PGDA your <strong>current tertiary institution</strong> must
            remain selected. You may add <strong>one more</strong> university.
            The current institution cannot be deselected; when two
            universities are selected, other options are not available.
          </Alert>
        </Grid>
      )}
      {isPgdaExternalUniversitiesForHdi && (
        <Grid item xs={12}>
          <Alert severity='info' variant='standard'>
            For PGDA, select <strong>one or two other universities</strong>{' '}.
            Your current HDI institution is already recorded on the
            Education step.
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <InputLabel htmlFor='institutions'>
          {isPgdaNonHdiFlow && !isPgdaExternalUniversitiesForHdi
            ? 'Universities for your PGDA application'
            : isPgdaExternalUniversitiesForHdi
              ? ''
              : isSingleInstitutionLocked
                ? ''
                : `Select a maximum of ${maxUni} universities you would like to study`}
        </InputLabel>

        <UniversityMultiselect
          formik={formik}
          maxSelection={maxUni}
          restrictToInstitutionIds={undefined}
          excludeInstitutionIds={
            isPgdaExternalUniversitiesForHdi
              ? [...EDUCATION_HDI_INSTITUTION_IDS_ORDERED]
              : undefined
          }
          lockedInstitutionIds={
            isPgdaNonHdiFlow && !isPgdaExternalUniversitiesForHdi && eduInstForPgda > 0
              ? [eduInstForPgda]
              : undefined
          }
          readOnlySelection={isSingleInstitutionLocked}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          id='qualificationStartYear'
          name='qualificationStartYear'
          label='Bursary application start year'
          value={formik.values.qualificationStartYear}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          type='text'
          disabled
          error={
            formik.touched.qualificationStartYear &&
            Boolean(formik.errors.qualificationStartYear)
          }
          helperText={
            formik.touched.qualificationStartYear &&
            formik.errors.qualificationStartYear
          }
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <YearOfStudy
          formik={formik}
          isDisabled={false}
          label='Year of study being applied for'
          removePGDA={false}
          institutionIdOverride={
            applicant.loginState.accessToken?.application?.education
              ?.institutionId
          }
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TypeOfStudy formik={formik} />
      </Grid>

      {formik.values.yearOfStudyId === PGDA_YEAR_OF_STUDY_ID && (
        <Grid item xs={12} md={8}>
          <FormControl>
            <FormLabel id='training-contract-label'>
              Do you currently have a training contract?
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby='training-contract-label'
              name='hasExistingContract'
              value={
                formik.values.hasExistingContract === true
                  ? 'yes'
                  : formik.values.hasExistingContract === false
                    ? 'no'
                    : ''
              }
              onChange={(e) => {
                const v = e.target.value;
                formik.setFieldValue('hasExistingContract', v === 'yes');
                if (v === 'no') {
                  formik.setFieldValue('firmName', '');
                }
              }}>
              <FormControlLabel
                value='yes'
                control={<Radio />}
                label='Yes'
              />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}

      {formik.values.yearOfStudyId === PGDA_YEAR_OF_STUDY_ID &&
        formik.values.hasExistingContract && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id='firmName'
                name='firmName'
                label='Firm you have a training contract with'
                value={formik.values.firmName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type='text'
                error={
                  formik.touched.firmName && Boolean(formik.errors.firmName)
                }
                helperText={
                  formik.touched.firmName && formik.errors.firmName
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity='warning' variant='standard'>
                Upload a copy of your training contract on the{' '}
                <strong>Documents</strong> step (document type &quot;Training
                contract&quot;) before you submit your application.
              </Alert>
            </Grid>
          </>
        )}

      <Grid item xs={12} mt={2}>
        <StepperButtons
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          formik={formik}
          status={applicant.applicantCreateState.status}
          isDisabled={false}
          setSaveAndClose={setIsSaveAndClose}
        />
      </Grid>
    </Grid>
  );
}
